import { useEffect, useRef, useState } from 'react';
import './Vision.css';

const API = process.env.REACT_APP_API_URL || "";

const FEATURES = [
    {
        id: '01',
        tag: 'Terrain Expansion',
        heading: 'Beyond the Plains',
        body: "Agri Drone Simulator will introduce diverse terrain environments — hilly landscapes, waterlogged paddy fields, uneven terrain, and sloped farmlands. Each terrain brings unique flight challenges that prepare operators for real-world conditions across India's varied agricultural geography.",
        key: 'vision-terrain',
        alt: 'Hilly agricultural terrain environment',
        side: 'right',
    },
    {
        id: '02',
        tag: 'Crop Library',
        heading: 'Expanding Crop Variety',
        body: 'The simulation will support an expanded library of crop varieties — paddy, sugarcane, cotton, wheat, and more — each with region-specific growth stages, canopy density, and spray requirements. Operators will train on the exact crop conditions they encounter in the field.',
        key: 'vision-crops',
        alt: 'Diverse crop fields in simulation',
        side: 'left',
    },
    {
        id: '03',
        tag: 'Tree Plantation',
        heading: 'Plantation & Orchard Operations',
        body: 'Tree plantation environments — mango orchards, coconut groves, eucalyptus plantations — introduce canopy navigation and precision application training. A distinct operational category that expands Agri Drone Simulator into plantation agriculture workflows.',
        key: 'vision-plantation',
        alt: 'Tree plantation drone operation',
        side: 'right',
    },
    {
        id: '04',
        tag: 'Damage & Breakage Logic',
        heading: 'Consequence-Based Training',
        body: 'Realistic drone damage and breakage simulation will be introduced — training operators to recognise the cost of poor flight decisions. Collision events, rotor strikes, and crash scenarios will carry simulated consequences, building the discipline required for real operations.',
        key: 'vision-damage',
        alt: 'Drone damage simulation scenario',
        side: 'left',
    },
    {
        id: '05',
        tag: 'Maintenance Module',
        heading: 'Pre-Flight & Post-Flight Protocols',
        body: 'A dedicated maintenance simulation module will guide operators through pre-flight checks, component inspection, battery management, and post-flight servicing — building operational discipline that directly reduces downtime and equipment failures in the field.',
        key: 'vision-maintenance',
        alt: 'Drone maintenance simulation interface',
        side: 'right',
    },
    {
        id: '06',
        tag: 'Drone Customization',
        heading: 'Build Your Configuration',
        body: 'Operators and institutions will configure drone models that match their real-world fleet — selecting spray systems, tank capacities, nozzle types, and frame configurations. Train on the exact machine you fly, before you fly it.',
        key: 'vision-customization',
        alt: 'Drone configuration and customization module',
        side: 'left',
    },
];

function useReveal() {
    const refs = useRef([]);
    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => entries.forEach(e => {
                if (e.isIntersecting) {
                    e.target.classList.add('sv-visible');
                    observer.unobserve(e.target);
                }
            }),
            { threshold: 0.12 }
        );
        refs.current.forEach(el => el && observer.observe(el));
        return () => observer.disconnect();
    }, []);
    return refs;
}

export default function Vision() {
    const [images, setImages] = useState({});

    useEffect(() => {
        fetch(`${API}/api/media/agrisimvision`)
            .then((res) => (res.ok ? res.json() : {}))
            .then((data) => setImages(data))
            .catch(() => { });
    }, []);

    const refs = useReveal();

    return (
        <section className="sv-wrap" id="vision">

            <div className="sv-bg-grid" aria-hidden="true" />

            <div className="sv-inner">

                {/* ── Header ── */}
                <header className="sv-header sv-reveal" ref={el => refs.current[99] = el}>
                    <div className="sv-eyebrow">
                        <span className="sv-eyebrow-dot" aria-hidden="true" />
                        Development Roadmap
                    </div>

                    <h2 className="sv-h2">
                        The Future of
                        <em>Agri Drone Training</em>
                    </h2>

                    <p className="sv-sub">
                        Agri Drone Simulator is a continuously evolving platform. Every milestone below is
                        part of our committed development pipeline — each one designed to bring
                        the simulation closer to the full complexity of real-world agri drone operations.
                    </p>

                    <div className="sv-status-bar">
                        <span className="sv-status-dot" aria-hidden="true" />
                        <span>All features currently in development</span>
                    </div>
                </header>

                {/* ── Feature rows ── */}
                <div className="sv-rows">
                    {FEATURES.map((f, i) => (
                        <article
                            key={f.id}
                            className={`sv-feat sv-feat--${f.side} sv-reveal`}
                            ref={el => refs.current[i] = el}
                        >
                            {/* Image column */}
                            <div className="sv-img-col">
                                <div className="sv-img-frame">
                                    <img
                                        src={images[f.key] || "/images/fallback.jpg"}
                                        alt={f.alt}
                                    />
                                    <div className="sv-img-ph" aria-hidden="true" />
                                    <span className="sv-ghost-num" aria-hidden="true">{f.id}</span>
                                    <div className="sv-cs-pill">
                                        <span className="sv-cs-dot" aria-hidden="true" />
                                        Coming Soon
                                    </div>
                                </div>
                            </div>

                            {/* Text column */}
                            <div className="sv-text-col">
                                <span className="sv-feat-tag">{f.tag}</span>
                                <h3 className="sv-feat-h3">{f.heading}</h3>
                                <p className="sv-feat-body">{f.body}</p>
                                <div className="sv-feat-rule" aria-hidden="true" />
                                <span className="sv-feat-num" aria-hidden="true">{f.id}</span>
                            </div>
                        </article>
                    ))}
                </div>

                {/* ── Footer ── */}
                <footer className="sv-footer sv-reveal" ref={el => refs.current[98] = el}>
                    <div className="sv-footer-rule" aria-hidden="true" />
                    <p className="sv-footer-text">
                        Feature availability timelines are communicated directly to institutional partners.{' '}
                        <a href="#contact" className="sv-footer-link">Contact us for early access →</a>
                    </p>
                </footer>

            </div>
        </section>
    );
}